/**
 * Utility formatters for the M1NT frontend
 */

import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns'
import { enUS } from 'date-fns/locale'

/** Parse API / UI date input to a valid Date or null */
const parseDateInput = (date) => {
  if (date == null || date === '') return null
  if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date
  if (typeof date === 'number') {
    const d = new Date(date)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof date === 'string') {
    const trimmed = date.trim()
    if (!trimmed) return null
    const isoParsed = parseISO(trimmed)
    if (isValid(isoParsed)) return isoParsed
    const d = new Date(trimmed)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(date)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Format date (US: MM/dd/yyyy)
 */
export const formatDate = (date) => {
  const d = parseDateInput(date)
  if (!d) return '-'
  return format(d, 'MM/dd/yyyy', { locale: enUS })
}

/**
 * Format date with time (US: MM/dd/yyyy, h:mm:ss AM/PM)
 */
export const formatDateTime = (date) => {
  const d = parseDateInput(date)
  if (!d) return '-'
  return format(d, 'MM/dd/yyyy, h:mm:ss a', { locale: enUS })
}

/**
 * Like formatDateTime but returns null when invalid (optional timeline fields).
 */
export const formatDateTimeOrNull = (date) => {
  const d = parseDateInput(date)
  if (!d) return null
  return format(d, 'MM/dd/yyyy, h:mm:ss a', { locale: enUS })
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date) => {
  const d = parseDateInput(date)
  if (!d) return '-'
  return formatDistanceToNow(d, { addSuffix: true, locale: enUS })
}

/**
 * Format ETH/MATIC value
 */
export const formatCurrency = (value, decimals = 4) => {
  if (value === null || value === undefined) return '-'
  const raw =
    typeof value === 'object' && value !== null && typeof value.toString === 'function'
      ? value.toString()
      : value
  const num = parseFloat(raw)
  if (isNaN(num)) return '-'
  return num.toFixed(decimals)
}

/**
 * Safe text for React children (avoids "Objects are not valid as a React child" from JSON / API fields).
 */
export const safeDisplayValue = (value) => {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

/**
 * Format wallet address
 */
export const formatAddress = (address) => {
  if (!address) return '-'
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

/**
 * Get IPFS gateway URL
 */
export const getIPFSUrl = (hash) => {
  if (!hash) return null
  // Remove ipfs:// prefix if present
  const cleanHash = hash.replace('ipfs://', '')
  return `https://gateway.pinata.cloud/ipfs/${cleanHash}`
}

/**
 * Normalize Pinata/IPFS URLs for browser display.
 * Fixes `https://gateway.pinata.cloud/<cid>` -> `.../ipfs/<cid>`.
 */
export const normalizeIpfsDisplayUrl = (url) => {
  if (!url || typeof url !== 'string') return url || null
  const u = url.trim()
  if (!u) return null
  if (u.startsWith('ipfs://')) return getIPFSUrl(u)
  if (u.startsWith('https://gateway.pinata.cloud/')) {
    if (u.startsWith('https://gateway.pinata.cloud/ipfs/')) return u
    const rest = u.replace('https://gateway.pinata.cloud/', '').replace(/^\/+/, '')
    if (/^(baf[a-z0-9]{20,}|Qm[a-zA-Z0-9]{40,})/.test(rest)) {
      return `https://gateway.pinata.cloud/ipfs/${rest}`
    }
  }
  return u
}

/** Chain ID from env (VITE_CHAIN_ID) */
const getChainId = () => {
  const id = import.meta.env.VITE_CHAIN_ID
  if (id === undefined || id === null) return null
  return typeof id === 'string' ? id : String(id)
}

/** True when using Amoy Testnet (80002) */
export const isTestnet = () => {
  const id = getChainId()
  return id === '80002' || id === 80002
}

/** Block explorer base URL (Amoy or Polygon Mainnet) */
export const getExplorerBaseUrl = () => {
  const url = import.meta.env.VITE_BLOCK_EXPLORER_URL
  if (url && typeof url === 'string' && url.trim()) return url.replace(/\/$/, '')
  return isTestnet() ? 'https://amoy.polygonscan.com' : 'https://polygonscan.com'
}

/**
 * Get OpenSea URL for token (testnet vs mainnet)
 */
export const getOpenSeaUrl = (contractAddress, tokenId) => {
  if (!contractAddress || !tokenId) return null
  if (isTestnet()) {
    return `https://testnets.opensea.io/assets/amoy/${contractAddress}/${tokenId}`
  }
  return `https://opensea.io/assets/matic/${contractAddress}/${tokenId}`
}

/**
 * Get PolygonScan/Amoy URL for transaction
 */
export const getPolygonscanUrl = (txHash) => {
  if (!txHash) return null
  return `${getExplorerBaseUrl()}/tx/${txHash}`
}

/**
 * Get PolygonScan/Amoy URL for token (NFT)
 */
export const getPolygonscanTokenUrl = (contractAddress, tokenId) => {
  if (!contractAddress || tokenId == null) return null
  return `${getExplorerBaseUrl()}/token/${contractAddress}?a=${tokenId}`
}

/**
 * NFT contract for links: prefer address stored at mint (metadata), else env.
 */
export const getMintContractAddressForCard = (card) => {
  const meta =
    card?.metadata && typeof card.metadata === 'object' && !Array.isArray(card.metadata)
      ? card.metadata
      : {}
  const fromMint =
    typeof meta.mintContractAddress === 'string' &&
    meta.mintContractAddress.startsWith('0x') &&
    meta.mintContractAddress.length >= 42
      ? meta.mintContractAddress
      : null
  const envAddr = import.meta.env.VITE_M1NT_CARD_ADDRESS
  return (
    fromMint || (typeof envAddr === 'string' && envAddr.startsWith('0x') ? envAddr : null) || null
  )
}

/**
 * Format card status for display
 */
export const formatCardStatus = (status) => {
  const statusMap = {
    PENDING: { label: 'Pending', color: 'yellow' },
    IN_GRADING: { label: 'In Grading', color: 'blue' },
    GRADED: { label: 'Graded', color: 'green' },
    MINTED: { label: 'Minted', color: 'purple' },
    BURNED: { label: 'Burned', color: 'orange' },
  }
  return statusMap[status] || { label: status, color: 'gray' }
}

/**
 * Format order status for display (status string only)
 */
export const formatOrderStatus = (status) => {
  const statusMap = {
    PENDING_QUOTE: { label: 'Minting under review', color: 'gray' },
    QUOTE_SENT: { label: 'Quote Sent', color: 'yellow' },
    AWAITING_PAYMENT: { label: 'Awaiting Payment', color: 'orange' },
    CREATED: { label: 'Created', color: 'gray' },
    PRICE_SET: { label: 'Price Set', color: 'yellow' },
    PAYMENT_PENDING: { label: 'Awaiting Payment', color: 'orange' },
    PAID: { label: 'Prepaid — Lab intake', color: 'green' },
    IN_PROGRESS: { label: 'In Progress', color: 'blue' },
    COMPLETED: { label: 'Completed', color: 'green' },
    NFT_MINTED: { label: 'NFT Minted', color: 'purple' },
    MINTED: { label: 'Minted', color: 'purple' },
    SHIPPED_TO_OWNER: { label: 'On the Way', color: 'blue' },
    DELIVERED: { label: 'Delivered', color: 'green' },
    AWAITING_SHIPMENT: { label: 'Ship to grader', color: 'orange' },
    SHIPPED_BY_OWNER: { label: 'Shipped by You', color: 'blue' },
    CARD_RECEIVED: { label: 'Received by grader', color: 'blue' },
    CANCELLED: { label: 'Cancelled', color: 'red' },
  }
  return statusMap[status] || { label: status, color: 'gray' }
}

/**
 * Get badge for order: show "Price Set" when priceUSD exists and status is QUOTE_SENT
 */
export const getOrderDisplayStatus = (order) => {
  if (!order) return { label: '—', color: 'gray' }
  if (order.paid && order.status === 'PAID') {
    return { label: 'Prepaid — Lab intake', color: 'green' }
  }
  // An M1NT credit marks the order paid before the Lab advances status to PAID
  if (order.paid && (order.status === 'PENDING_QUOTE' || order.status === 'CREATED')) {
    return { label: 'Awaiting lab review', color: 'yellow' }
  }
  if (order.paid) return { label: 'Paid', color: 'green' }
  if (order.priceUSD != null && order.status === 'QUOTE_SENT') {
    return { label: 'Price Set', color: 'yellow' }
  }
  if (order.status === 'PENDING_QUOTE') return { label: 'Minting under review', color: 'gray' }
  return formatOrderStatus(order.status)
}

/**
 * Format grade for display.
 * Uses one decimal when needed (e.g. 5.5/10). API may send `grade` as rounded Int
 * and `gradeOverall` as the true Decimal — prefer gradeOverall in callers.
 * Prisma Decimal often serializes as string in JSON.
 */
export const formatGrade = (grade) => {
  if (grade === null || grade === undefined || grade === '') return '-'
  let n
  if (typeof grade === 'number') {
    if (Number.isNaN(grade)) return '-'
    n = grade
  } else if (typeof grade === 'string') {
    const t = grade.trim()
    if (!t) return '-'
    n = parseFloat(t.replace(',', '.'))
  } else if (typeof grade === 'object' && grade !== null && typeof grade.toString === 'function') {
    n = parseFloat(String(grade).replace(',', '.'))
  } else {
    n = Number(grade)
  }
  if (!Number.isFinite(n)) return '-'
  const rounded = Math.round(n * 10) / 10
  const display =
    Math.abs(rounded - Math.trunc(rounded)) < 1e-9
      ? String(Math.trunc(rounded))
      : rounded.toFixed(1)
  return `${display}/10`
}

/** Final grade from an order; shows "Not graded" / "Authentic" for no-grade modes. */
export const formatOrderFinalGrade = (order) => {
  if (!order || typeof order !== 'object') return '-'
  if (order.gradingMode === 'RAW_ENCAPSULATION') return 'Not graded'
  if (order.gradingMode === 'AUTHENTIC') return 'Authentic'
  return formatGrade(order.gradeOverall ?? order.grade)
}
